import { forwardRef } from 'react'
import clsx from 'clsx'

// Variants & sizes
type MarketingButtonVariant = 'primary' | 'secondary'
type MarketingButtonSize = 'sm' | 'md' | 'lg'

// Shared props
interface CommonProps {
  variant?: MarketingButtonVariant
  size?: MarketingButtonSize
  className?: string
}

// Button props
interface ButtonProps extends CommonProps, React.ButtonHTMLAttributes<HTMLButtonElement> {
  as?: 'button'
}

// Anchor props
interface AnchorProps extends CommonProps, React.AnchorHTMLAttributes<HTMLAnchorElement> {
  as: 'a'
}

export type MarketingButtonProps = ButtonProps | AnchorProps

export const MarketingButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  MarketingButtonProps
>(function MarketingButton(
  { as = 'button', variant = 'primary', size = 'md', className, ...props },
  ref,
) {
  const Component = as

  return (
    <Component
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ref={ref as any}
      className={clsx(
        'inline-flex items-center justify-center',
        'rounded-br-lg',
        'font-medium',
        'transition-colors duration-200',
        'cursor-pointer',
        'no-underline',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2e76bc]',

        size === 'sm' && 'px-3 py-1.5 text-xs',
        size === 'md' && 'px-6 py-2 text-sm',
        size === 'lg' && 'px-8 py-3 text-base',

        variant === 'primary' && [
          'bg-[#2e76bc]',
          'text-white',
          'border border-[#2e76bc]',
          'hover:bg-[#0A263B] hover:border-[#2e76bc]',
        ],

        variant === 'secondary' && [
          'bg-white',
          'text-[#0B2948]',
          'border border-blue-200',
          'hover:bg-blue-200 hover:border-blue-300',
        ],

        className,
      )}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(props as any)}
    />
  )
})
